import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { uid } from "@/lib/uid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Video, Trash2, Plus, ChevronDown, ChevronRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LEVEL_STYLES = {
  accettabile: "bg-emerald-100 text-emerald-700",
  attenzione: "bg-amber-100 text-amber-700",
  rischio: "bg-rose-100 text-rose-700",
};

function emptyContext() {
  return { peso_kg: 10, frequenza_min: 1, forza_extra_kg: 0, descrizione_sollevamento: "" };
}

/**
 * Enhanced NIOSH module — multi-video multitask CLI (ISO 11228-1).
 * Accepts multiple videos, each representing a distinct lifting subtask,
 * plus a rich task-level context (mansion, duration, environment, notes).
 */
export default function StepNioshMultitask({ data, onChange, aiLoading, setAiLoading, mansioniOptions = [] }) {
  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Multitask analyses (metodo === "niosh_multitask")
  const tasks = useMemo(() => list.filter((x) => x?.metodo === "niosh_multitask"), [list]);
  const legacy = useMemo(() => list.filter((x) => x?.metodo !== "niosh_multitask"), [list]);

  // Pending new task form
  const [pending, setPending] = useState({
    task_name: "",
    descrizione: "",
    mansione: "",
    durata_ore_giorno: 8,
    ambiente: "",
    note: "",
    videos: [],   // Array<File>
    contesti: [], // Array<{peso_kg, frequenza_min, forza_extra_kg, descrizione_sollevamento}>
  });
  const fileRef = useRef(null);

  const addVideos = (files) => {
    const arr = Array.from(files || []);
    setPending((p) => ({
      ...p,
      videos: [...p.videos, ...arr],
      contesti: [...p.contesti, ...arr.map(emptyContext)],
    }));
  };

  const removeVideo = (i) => {
    setPending((p) => ({
      ...p,
      videos: p.videos.filter((_, idx) => idx !== i),
      contesti: p.contesti.filter((_, idx) => idx !== i),
    }));
  };

  const updateContesto = (i, patch) => {
    setPending((p) => ({
      ...p,
      contesti: p.contesti.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    }));
  };

  const analyze = async () => {
    if (!pending.task_name.trim()) return toast.error("Inserisci il nome del task");
    if (pending.videos.length === 0) return toast.error("Carica almeno un video");
    setAiLoading(true);
    const fd = new FormData();
    fd.append("task_name", pending.task_name);
    fd.append("descrizione", pending.descrizione);
    fd.append("mansione", pending.mansione);
    fd.append("durata_ore_giorno", String(pending.durata_ore_giorno));
    fd.append("ambiente", pending.ambiente);
    fd.append("note", pending.note);
    fd.append("contesto_json", JSON.stringify(pending.contesti));
    pending.videos.forEach((v) => fd.append("files", v));
    try {
      const r = await api.post("/dvr/ai/niosh-multitask", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      onChange([...list, { ...r.data, _uid: uid() }]);
      setPending({
        task_name: "",
        descrizione: "",
        mansione: "",
        durata_ore_giorno: 8,
        ambiente: "",
        note: "",
        videos: [],
        contesti: [],
      });
      toast.success("Task NIOSH multitask analizzato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore AI");
    }
    setAiLoading(false);
  };

  const removeTask = (uidToRemove) => onChange(list.filter((t) => t._uid !== uidToRemove));

  return (
    <div className="space-y-6">
      <div className="text-sm text-muted-foreground">
        Metodo NIOSH multitask (ISO 11228-1) — carica <b>più video di sollevamenti diversi</b> eseguiti dalla stessa mansione.
        L'AI analizzerà ogni sollevamento singolarmente e calcolerà il <b>CLI (Composite Lifting Index)</b> ufficiale.
      </div>

      {/* === Form nuovo task === */}
      <div className="border border-border rounded-lg p-4 bg-muted/30 space-y-3" data-testid="niosh-mt-form">
        <div className="font-semibold text-sm">Nuovo task multitask</div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Nome task *</Label>
            <Input
              value={pending.task_name}
              onChange={(e) => setPending({ ...pending, task_name: e.target.value })}
              placeholder="Es. Carico/scarico cassette da pallet"
              data-testid="niosh-mt-name"
            />
          </div>
          <div>
            <Label>Mansione</Label>
            {mansioniOptions.length > 0 ? (
              <select
                className="w-full border border-border rounded-md h-9 px-2 text-sm bg-background"
                value={pending.mansione}
                onChange={(e) => setPending({ ...pending, mansione: e.target.value })}
                data-testid="niosh-mt-mansione"
              >
                <option value="">— seleziona —</option>
                {mansioniOptions.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <Input
                value={pending.mansione}
                onChange={(e) => setPending({ ...pending, mansione: e.target.value })}
                placeholder="Es. Magazziniere"
                data-testid="niosh-mt-mansione"
              />
            )}
          </div>
        </div>

        <div>
          <Label>Descrizione del task</Label>
          <Textarea
            rows={2}
            value={pending.descrizione}
            onChange={(e) => setPending({ ...pending, descrizione: e.target.value })}
            placeholder="Descrivi in dettaglio l'attività: cosa fa il lavoratore, per quanto tempo, con quale attrezzatura, in che ambiente…"
            data-testid="niosh-mt-descrizione"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Durata (ore/giorno)</Label>
            <Input
              type="number"
              step="0.5"
              value={pending.durata_ore_giorno}
              onChange={(e) => setPending({ ...pending, durata_ore_giorno: Number(e.target.value) })}
              data-testid="niosh-mt-durata"
            />
          </div>
          <div className="col-span-2">
            <Label>Ambiente di lavoro</Label>
            <Input
              value={pending.ambiente}
              onChange={(e) => setPending({ ...pending, ambiente: e.target.value })}
              placeholder="Es. Magazzino, temperatura 22°C, pavimento asciutto"
              data-testid="niosh-mt-ambiente"
            />
          </div>
        </div>

        <div>
          <Label>Note operatore (contesto, dettagli non visibili nei video)</Label>
          <Textarea
            rows={2}
            value={pending.note}
            onChange={(e) => setPending({ ...pending, note: e.target.value })}
            data-testid="niosh-mt-note"
          />
        </div>

        {/* Videos upload */}
        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-sm">Video sollevamenti ({pending.videos.length})</div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => fileRef.current?.click()}
              data-testid="niosh-mt-add-video"
            >
              <Plus className="w-4 h-4 mr-1" /> Aggiungi video
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="video/*"
              multiple
              hidden
              onChange={(e) => addVideos(e.target.files)}
            />
          </div>

          {pending.videos.length === 0 ? (
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-border rounded-md p-6 text-sm text-muted-foreground hover:bg-muted/30 flex flex-col items-center gap-2"
              data-testid="niosh-mt-upload-zone"
            >
              <Video className="w-6 h-6" />
              Trascina o clicca per caricare uno o più video di sollevamento
            </button>
          ) : (
            <div className="space-y-2">
              {pending.videos.map((v, i) => (
                <div key={v.name + i} className="border border-border rounded-md p-3 bg-background">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold truncate">
                      #{i + 1} {v.name} <span className="text-xs text-muted-foreground">({(v.size / 1024 / 1024).toFixed(1)} MB)</span>
                    </div>
                    <button
                      onClick={() => removeVideo(i)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`niosh-mt-remove-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-2">
                      <Label className="text-xs">Descrizione sollevamento</Label>
                      <Input
                        value={pending.contesti[i]?.descrizione_sollevamento || ""}
                        onChange={(e) => updateContesto(i, { descrizione_sollevamento: e.target.value })}
                        placeholder="Es. presa da pallet a terra"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Peso (kg)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={pending.contesti[i]?.peso_kg ?? 10}
                        onChange={(e) => updateContesto(i, { peso_kg: Number(e.target.value) })}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Frequenza (lift/min)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={pending.contesti[i]?.frequenza_min ?? 1}
                        onChange={(e) => updateContesto(i, { frequenza_min: Number(e.target.value) })}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            onClick={analyze}
            disabled={aiLoading || pending.videos.length === 0 || !pending.task_name.trim()}
            className="rounded-full"
            data-testid="niosh-mt-analyze"
          >
            {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Analizza task ({pending.videos.length} video)
          </Button>
        </div>
      </div>

      {/* === Task analizzati === */}
      <div className="space-y-4">
        {tasks.map((t) => (
          <TaskCard key={t._uid} task={t} onRemove={() => removeTask(t._uid)} />
        ))}
        {legacy.length > 0 && (
          <details className="text-xs text-muted-foreground border border-dashed border-border rounded p-2">
            <summary className="cursor-pointer">Analisi legacy singolo video ({legacy.length})</summary>
            <div className="pt-2 space-y-1">
              {legacy.map((n, i) => (
                <div key={n._uid || `legacy-${i}`} className="flex items-center justify-between">
                  <span>LI = {n.LI ?? "?"} · {n.classificazione || "—"} · {n.commento?.slice(0, 80) || ""}</span>
                  <button
                    onClick={() => onChange(list.filter((_, idx) => list.indexOf(n) !== idx || n._uid !== list[idx]?._uid))}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function TaskCard({ task, onRemove }) {
  const [open, setOpen] = useState(false);
  const s = task.sintesi || {};
  const level = (s.livello_rischio || "attenzione").toLowerCase();
  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid={`niosh-mt-task-${task._uid}`}>
      <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex-1">
          <div className="font-semibold">{task.task_name}</div>
          <div className="text-xs opacity-70">
            {task.mansione ? `Mansione: ${task.mansione} · ` : ""}
            {(task.sollevamenti || []).length} sollevamenti · {task.durata_ore_giorno} h/gg
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded ${LEVEL_STYLES[level] || "bg-slate-200 text-slate-700"}`}>
            CLI {s.CLI?.toFixed?.(2) ?? "?"} · {level.toUpperCase()}
          </span>
          <button onClick={() => setOpen(!open)} className="text-white/80 hover:text-white">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <button onClick={onRemove} className="text-white/80 hover:text-rose-300" data-testid={`niosh-mt-task-remove-${task._uid}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {open && (
        <div className="p-4 space-y-4 bg-background">
          {(task.sollevamenti || []).map((sub, i) => (
            <SubtaskBlock key={`n-${i}`} sub={sub} idx={i + 1} />
          ))}
          {s.commento_globale && (
            <div className="p-3 bg-muted/40 rounded border-l-4 border-slate-700">
              <div className="text-xs font-semibold mb-1">Commento tecnico globale</div>
              <div className="text-sm">{s.commento_globale}</div>
            </div>
          )}
          {s.misure_preventive?.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-1">Misure preventive (ISO 11228-1)</div>
              <ul className="text-sm list-disc pl-6 space-y-1">
                {s.misure_preventive.map((m, i) => <li key={`m-${i}`}>{m}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SubtaskBlock({ sub, idx }) {
  const level = (sub.livello_rischio || "attenzione").toLowerCase();
  return (
    <div className="border border-border rounded-md p-3 bg-muted/20">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm">
          Sollevamento {idx}: {sub.descrizione_sollevamento || sub.fase_lavorativa || "—"}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded ${LEVEL_STYLES[level] || "bg-slate-200"}`}>
          STLI {sub.STLI?.toFixed?.(2) ?? "?"}
        </span>
      </div>
      {sub.frames?.length > 0 && (
        <div className="flex gap-2 overflow-x-auto mb-2">
          {sub.frames.slice(0, 4).map((u) => (
            <img key={u} src={`${BACKEND}${u}`} alt="frame" className="h-24 rounded border" />
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-slate-50 rounded p-2">
          <div className="font-semibold mb-1">Parametri</div>
          <div>H: {sub.parametri?.H_cm} cm · V: {sub.parametri?.V_cm} cm · D: {sub.parametri?.D_cm} cm</div>
          <div>A: {sub.parametri?.A_gradi}° · F: {sub.parametri?.F_liftmin}/min · C: {sub.parametri?.C_qualita_presa}</div>
          <div>Peso: {sub.peso_effettivo_kg} kg · RWL: {sub.RWL_kg?.toFixed?.(2)} kg</div>
        </div>
        <div className="bg-slate-50 rounded p-2">
          <div className="font-semibold mb-1">Moltiplicatori</div>
          <div>HM {sub.moltiplicatori?.HM?.toFixed?.(2)} · VM {sub.moltiplicatori?.VM?.toFixed?.(2)}</div>
          <div>DM {sub.moltiplicatori?.DM?.toFixed?.(2)} · AM {sub.moltiplicatori?.AM?.toFixed?.(2)}</div>
          <div>FM {sub.moltiplicatori?.FM?.toFixed?.(2)} · CM {sub.moltiplicatori?.CM?.toFixed?.(2)}</div>
        </div>
      </div>
      {sub.criticita_osservate?.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-semibold text-rose-700">Criticità</div>
          <ul className="text-xs list-disc pl-5">
            {sub.criticita_osservate.map((c, i) => <li key={`c-${i}`}>{c}</li>)}
          </ul>
        </div>
      )}
      {sub.commento_tecnico && (
        <div className="text-xs mt-2 italic text-slate-700">{sub.commento_tecnico}</div>
      )}
    </div>
  );
}
